import { Link } from 'react-router-dom'
import { Seo } from '../components/Seo'
import { PageHeader } from '../components/common/PageHeader'
import { Reveal } from '../components/animation/Reveal'
import { Icon } from '../components/common/Icon'
import { langLabel } from '../components/product/ProductFactPanels'
import { useProductsDetailed } from '../hooks'
import { categoryBySlug } from '../lib/categories'
import { extractProductFacts } from '../lib/productFacts'
import { breadcrumbSchema, collectionPageSchema, documentListSchema } from '../lib/seo'

/**
 * Usage guides, the technical-document library and the approvals register.
 *
 * THREE THINGS THIS PAGE FIXES.
 *
 * 1. The data sheets were orphans. Every product carries an English and a Hindi PDF, and the only way
 *    to reach one was to already be on that product's page. Nothing listed them, so nothing crawled
 *    them as a set and no visitor could see that the documents existed at all.
 *
 * 2. Approvals were stated in one place as a company-wide list. They are not company-wide: the AGN
 *    family is NPOP-approved and FCO-registered, Biovin holds an EU organic approval, Hydretain is
 *    USDA BioPreferred. Printed as one list under one heading, that reads as "everything is approved
 *    under everything". Here each approval sits on the row of the product that holds it, quoted from
 *    that product's own record.
 *
 * 3. The guide text used to name products and shelf lives inline, so it drifted from the catalogue the
 *    moment either changed. The table below is generated from the records.
 */

const GUIDES = [
  {
    icon: 'Sprout',
    title: 'Applying soil bio-fertilizers',
    body: 'Mix liquid bio-fertilizers with water at the ratio on the label and, where the label calls for it, incubate before applying — the step activates dormant spores. Apply by drench, flood or drip so the product reaches the root zone. Keep the interval the label specifies from any fungicide, pesticide or weedicide.',
    link: { to: '/knowledge/bio-fertilizers', label: 'What bio-fertilizers are and how they work' },
  },
  {
    icon: 'Leaf',
    title: 'Foliar spraying best practice',
    body: 'Spray in the early morning or evening so the deposit stays moist — uptake continues only while it does. Cover all foliage evenly at the water volume the label states, and where a product gives a window between mixing and spraying, apply within it.',
    link: { to: '/knowledge/foliar-and-soil-application', label: 'Foliar, soil and fertigation compared' },
  },
  {
    icon: 'Droplets',
    title: 'Fertigation & drenching',
    body: 'Fertigation needs a fully soluble, compatible product and care over mixing order — a precipitate blocks emitters. Run the set long enough to carry the input into the root zone. Concentrates must be diluted to at least the ratio on the label before they go into the line.',
    link: { to: '/knowledge/rhizosphere', label: 'Why the root zone is the target' },
  },
  {
    icon: 'Package',
    title: 'Storage & shelf life',
    body: 'Store products dry, frost-free and out of direct sunlight. Shelf life is printed on each pack and listed per product in the table below — for a living product it is the period over which the stated microbial count is expected to hold.',
    link: { to: '/catalog', label: 'Shelf life by product' },
  },
]

export default function ResourcesPage() {
  const { data: products = [], isLoading } = useProductsDetailed()

  const rows = products.map((p) => ({
    product: p,
    facts: extractProductFacts(p),
    category: categoryBySlug(p.categorySlug),
  }))
  const documents = rows.flatMap((r) =>
    (r.product.resources ?? []).map((d) => ({
      productName: r.product.name,
      productSlug: r.product.slug,
      label: d.label,
      url: d.url,
      lang: d.lang,
    })),
  )
  const withoutDocuments = rows.filter((r) => !(r.product.resources ?? []).length).map((r) => r.product.name)
  const withoutApproval = rows.filter((r) => !r.facts.licence).map((r) => r.product.name)

  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Resources', path: '/resources' },
  ]

  return (
    <>
      <Seo
        title="Usage Guides, Technical Data Sheets & Approvals"
        description="Application guidance for the GreenbayBio range, the technical data sheet for every product in English and Hindi, and the approval each product holds — stated product by product."
        path="/resources"
        keywords={['technical data sheet', 'product label', 'NPOP FCO approval', 'application guide']}
        jsonLd={[
          collectionPageSchema({
            name: 'GreenbayBio resources — guides, data sheets and approvals',
            description:
              'Application guidance, the technical data sheet for every GreenbayBio product, and the approval each product holds.',
            path: '/resources',
          }),
          ...(documents.length ? [documentListSchema('/resources', documents)] : []),
          breadcrumbSchema(crumbs),
        ]}
      />
      <PageHeader
        eyebrow="Guides, data sheets & approvals"
        title="Everything the label says, in one place."
        subtitle="Practical application guidance, the technical data sheet for every product, and the approval each product holds. The printed label on your pack always governs."
      />

      <div className="container-x py-14">
        <Reveal className="grid gap-5 md:grid-cols-2" stagger={0.1} y={24}>
          {GUIDES.map((g) => (
            <div key={g.title} className="rounded-xl border border-sand-200 bg-white p-7 shadow-soft">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                <Icon name={g.icon} className="h-6 w-6" />
              </span>
              <h2 className="mt-5 font-display text-xl font-semibold text-ink">{g.title}</h2>
              <p className="mt-2 leading-relaxed text-ink-500">{g.body}</p>
              <Link
                to={g.link.to}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:underline"
              >
                {g.link.label}
                <Icon name="ArrowRight" className="h-4 w-4" />
              </Link>
            </div>
          ))}
        </Reveal>

        {/* ── the register: one row per product, facts from its own record ── */}
        <section className="mt-16" aria-labelledby="register-heading">
          <h2 id="register-heading" className="font-display text-h2 font-semibold text-ink">
            Product register — application, shelf life, approval and data sheet
          </h2>
          <p className="mt-2 max-w-3xl text-ink-500">
            Every value below is read from that product&rsquo;s own catalogue record. An approval applies to the
            product on its row and to no other.
          </p>

          <div className="mt-6 overflow-x-auto rounded-xl border border-sand-200">
            <table className="w-full min-w-[52rem] border-collapse text-sm">
              <caption className="sr-only">
                GreenbayBio products with category, application method, shelf life, approval and technical documents
              </caption>
              <thead>
                <tr className="bg-sand-100/70 text-left">
                  <th scope="col" className="px-5 py-3 font-semibold text-ink-700">Product</th>
                  <th scope="col" className="px-5 py-3 font-semibold text-ink-700">Category</th>
                  <th scope="col" className="px-5 py-3 font-semibold text-ink-700">Application</th>
                  <th scope="col" className="px-5 py-3 font-semibold text-ink-700">Shelf life</th>
                  <th scope="col" className="px-5 py-3 font-semibold text-ink-700">Approval / licence</th>
                  <th scope="col" className="px-5 py-3 font-semibold text-ink-700">Data sheet</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ product, facts, category }) => (
                  <tr key={product.slug} className="border-t border-sand-200 align-top odd:bg-white even:bg-canvas/50">
                    <th scope="row" className="px-5 py-4 text-left font-medium">
                      <Link to={`/catalog/${product.slug}`} className="text-brand-700 hover:underline">
                        {product.name}
                      </Link>
                      {product.tagline && (
                        <span className="mt-0.5 block text-xs font-normal text-ink-400">{product.tagline}</span>
                      )}
                    </th>
                    <td className="px-5 py-4 text-ink-500">
                      {category ? (
                        <Link to={`/product-categories/${category.slug}`} className="hover:text-brand-700">
                          {category.name}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-5 py-4 text-ink-500">{facts.applicationMethods.join(', ') || '—'}</td>
                    <td className="px-5 py-4 text-ink-500">{facts.shelfLife ?? '—'}</td>
                    <td className="px-5 py-4 text-ink-500">{facts.licence ?? 'Not stated on this product’s record'}</td>
                    <td className="px-5 py-4">
                      {(product.resources ?? []).length ? (
                        <ul className="space-y-1">
                          {(product.resources ?? []).map((d, i) => (
                            <li key={`${d.url}-${i}`}>
                              <a
                                href={d.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                {...(d.lang ? { hrefLang: d.lang } : {})}
                                className="inline-flex items-center gap-1.5 font-medium text-brand-700 hover:underline"
                              >
                                <Icon name="FileText" className="h-3.5 w-3.5 shrink-0" />
                                {d.lang ? langLabel(d.lang) : d.label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-ink-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isLoading && <p className="mt-4 text-sm text-ink-500">Loading the product register…</p>}

          {/* Gaps named rather than left as dashes a reader has to interpret. */}
          {(withoutDocuments.length > 0 || withoutApproval.length > 0) && !isLoading && (
            <div className="mt-4 space-y-2">
              {withoutDocuments.length > 0 && (
                <p className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <Icon name="AlertCircle" className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <span>
                    No data sheet is attached for {withoutDocuments.join(', ')}. The product page carries the
                    same composition, dose and precaution detail; ask us for the printed sheet.
                  </span>
                </p>
              )}
              {withoutApproval.length > 0 && (
                <p className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <Icon name="AlertCircle" className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <span>
                    No approval or licence is recorded against {withoutApproval.join(', ')}. That means this
                    site does not state one — not that none exists. Contact us for the current position.
                  </span>
                </p>
              )}
            </div>
          )}

          <p className="mt-4 text-xs text-ink-500">
            Registration under India&rsquo;s Fertiliser (Control) Order and approval for use in organic farming
            under NPOP are separate instruments, and an approval held under an overseas standard is not Indian
            regulatory approval. Each row states the wording recorded for that product.{' '}
            <Link to="/knowledge/bio-fertilizers" className="text-brand-700 hover:underline">
              How the two differ
            </Link>
            .
          </p>
        </section>
      </div>
    </>
  )
}
